import {
  Stack,
  TextInput,
  Textarea,
  Group,
  Button,
  ActionIcon,
  Tooltip,
  Text,
  Card,
  TagsInput,
  Select,
  Divider,
  Title,
  Grid,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  IconCopy,
  IconCheck,
  IconDeviceFloppy,
  IconSparkles,
} from "@tabler/icons-react";
import { useEffect, useCallback, useState, useRef } from "react";
import { useBrandKitStore } from "./brandKitStore";
import { useBrandsStore } from "../Brands/brandsStore";
import BeaconLoader from "../_Shared/BeaconLoader";
import DirectoryChecklist from "./DirectoryChecklist";
import ContentGuidelinesEditor from "./ContentGuidelinesEditor";
import { useIsGlobalAdmin } from "../_Shared/useIsGlobalAdmin";

const PRICING_MODELS = [
  { value: "Free", label: "Free" },
  { value: "Freemium", label: "Freemium" },
  { value: "Paid", label: "Paid" },
  { value: "Free Trial", label: "Free Trial" },
  { value: "Open Source", label: "Open Source" },
  { value: "Contact for Pricing", label: "Contact for Pricing" },
];

/** Lightweight copy helper — no per-instance hooks */
function CopyableField({ label, value, maxLength, copiedField, onCopy, children }) {
  const isCopied = copiedField === label;

  return (
    <Group align="flex-end" gap="xs" wrap="nowrap">
      <div style={{ flex: 1 }}>
        {children}
        {maxLength && (
          <Text size="xs" c={value?.length > maxLength ? "red" : "dimmed"} mt={2}>
            {value?.length || 0} / {maxLength}
          </Text>
        )}
      </div>
      <Tooltip label={isCopied ? "Copied!" : `Copy ${label}`}>
        <ActionIcon
          variant="subtle"
          color={isCopied ? "teal" : "gray"}
          onClick={() => onCopy(label, value || "")}
          mb={maxLength ? 20 : 0}
        >
          {isCopied ? <IconCheck size={16} /> : <IconCopy size={16} />}
        </ActionIcon>
      </Tooltip>
    </Group>
  );
}

export default function BrandKitEditor({ brand }) {
  const { kit, isLoading, isSaving, isGenerating, isGeneratingVisualStyle, fetchKit, saveKit, generateKit, generateVisualStyle } = useBrandKitStore();
  const { updateBrand } = useBrandsStore();
  const isGlobalAdmin = useIsGlobalAdmin();

  const form = useForm({
    initialValues: {
      valuePropositions: brand.valuePropositions || "",
      tagline: "",
      shortDescription: "",
      longDescription: "",
      pricingModel: "",
      price: "",
      category: "",
      makersComment: "",
      tags: [],
      logoUrls: [],
      screenshotUrls: [],
      directories: [],
      contentGuidelines: [],
      defaultTranslationMode: "Translate",
      visualStyle: {
        style: "",
        colorPalette: "",
        mood: "",
        additionalInstructions: "",
        defaultImagePrompt: "",
      },
    },
  });

  useEffect(() => {
    fetchKit(brand.id);
  }, [brand.id]);

  useEffect(() => {
    if (kit) {
      form.setValues({
        valuePropositions: brand.valuePropositions || "",
        tagline: kit.tagline || "",
        shortDescription: kit.shortDescription || "",
        longDescription: kit.longDescription || "",
        pricingModel: kit.pricingModel || "",
        price: kit.price || "",
        category: kit.category || "",
        makersComment: kit.makersComment || "",
        tags: kit.tags || [],
        logoUrls: kit.logoUrls || [],
        screenshotUrls: kit.screenshotUrls || [],
        directories: kit.directories || [],
        contentGuidelines: kit.contentGuidelines || [],
        defaultTranslationMode: kit.defaultTranslationMode || "Translate",
        visualStyle: {
          // Spread first so the fields this form has no input for — motif, lighting,
          // composition, doNot, defaultAspectRatio, defaultModel — survive the round trip.
          // upsert_visual_style and the "Suggest visual style" endpoint write them, and
          // generate_image reads defaultAspectRatio/defaultModel; the PUT rebuilds VisualStyle
          // from what we send, so anything dropped here is deleted on the next save.
          ...kit.visualStyle,
          style: kit.visualStyle?.style || "",
          colorPalette: kit.visualStyle?.colorPalette || "",
          mood: kit.visualStyle?.mood || "",
          additionalInstructions: kit.visualStyle?.additionalInstructions || "",
          defaultImagePrompt: kit.visualStyle?.defaultImagePrompt || "",
        },
      });
    }
  }, [kit]);

  const handleSave = async () => {
    const { valuePropositions, ...kitValues } = form.values;
    await saveKit(brand.id, kitValues);
    if (valuePropositions !== (brand.valuePropositions || "")) {
      await updateBrand(brand.id, { ...brand, valuePropositions });
    }
  };

  const handleGenerateVisualStyle = async () => {
    try {
      const generated = await generateVisualStyle(brand.id);
      form.setValues((prev) => {
        // Keep every suggested field, not just the five with inputs on this form. The prompt
        // asks for motif, lighting, composition and a default aspect ratio too, and those are
        // lost on the next save unless they are held here. A blank suggestion keeps the
        // current value rather than clearing it.
        const merged = { ...prev.visualStyle };
        for (const [key, value] of Object.entries(generated || {})) {
          if (value) merged[key] = value;
        }
        return { ...prev, visualStyle: merged };
      });
    } catch {
      // notification already shown by store
    }
  };

  const handleGenerate = async () => {
    try {
      const generated = await generateKit(brand.id);
      form.setValues((prev) => ({
        ...prev,
        tagline: generated.tagline || prev.tagline,
        shortDescription: generated.shortDescription || prev.shortDescription,
        longDescription: generated.longDescription || prev.longDescription,
        pricingModel: generated.pricingModel || prev.pricingModel,
        price: generated.price || prev.price,
        category: generated.category || prev.category,
        makersComment: generated.makersComment || prev.makersComment,
        tags: generated.tags?.length ? generated.tags : prev.tags,
      }));
    } catch {
      // notification already shown by store
    }
  };

  const handleDirectoriesChange = useCallback((dirs) => {
    form.setFieldValue("directories", dirs);
  }, []);

  // Single copy state shared by all CopyableField instances (replaces 12+ useClipboard hooks)
  const [copiedField, setCopiedField] = useState(null);
  const copiedTimeout = useRef(null);

  const handleCopy = useCallback((field, value) => {
    navigator.clipboard.writeText(value);
    setCopiedField(field);
    clearTimeout(copiedTimeout.current);
    copiedTimeout.current = setTimeout(() => setCopiedField(null), 1500);
  }, []);

  // Compute full block lazily on click instead of every render
  const handleCopyAll = useCallback(() => {
    const block = [
      `Name: ${brand.name}`,
      brand.websiteUrl ? `URL: ${brand.websiteUrl}` : null,
      form.values.valuePropositions ? `Value propositions: ${form.values.valuePropositions}` : null,
      form.values.tagline ? `Tagline: ${form.values.tagline}` : null,
      form.values.shortDescription ? `Short Description: ${form.values.shortDescription}` : null,
      form.values.longDescription ? `Long Description: ${form.values.longDescription}` : null,
      form.values.pricingModel
        ? `Pricing: ${form.values.pricingModel}${form.values.price ? ` (${form.values.price})` : ""}`
        : null,
      form.values.tags?.length ? `Tags: ${form.values.tags.join(", ")}` : null,
      brand.competitors?.length
        ? `Alternatives: ${brand.competitors.map((c) => c.name).join(", ")}`
        : null,
      form.values.category ? `Category: ${form.values.category}` : null,
    ]
      .filter(Boolean)
      .join("\n");

    handleCopy("__all__", block);
  }, [brand, form, handleCopy]);

  if (isLoading) {
    return <BeaconLoader size={48} />;
  }

  const allCopied = copiedField === "__all__";

  return (
    <Stack gap="lg" mt="md">
      <Group
        justify="space-between"
        align="center"
        style={{
          position: "sticky",
          top: 60,
          zIndex: 10,
          backgroundColor: "var(--mantine-color-body)",
          paddingBlock: 8,
          marginInline: -4,
          paddingInline: 4,
        }}
      >
          <div>
            <Title order={4}>Brand Kit</Title>
            <Text size="sm" c="dimmed">
              What every article must say. Setup is how you start monitoring.
            </Text>
          </div>
        <Group>
          <Button
            variant="light"
            size="xs"
            leftSection={<IconSparkles size={14} />}
            onClick={handleGenerate}
            loading={isGenerating}
            color="violet"
          >
            Generate with AI
          </Button>
          <Tooltip label={allCopied ? "Copied!" : "Copy all fields"}>
            <Button
              variant="light"
              size="xs"
              leftSection={allCopied ? <IconCheck size={14} /> : <IconCopy size={14} />}
              onClick={handleCopyAll}
              color={allCopied ? "teal" : "gray"}
            >
              {allCopied ? "Copied!" : "Copy All"}
            </Button>
          </Tooltip>
          <Button
            leftSection={<IconDeviceFloppy size={16} />}
            onClick={handleSave}
            loading={isSaving}
          >
            Save
          </Button>
        </Group>
      </Group>

      {/* Read-only fields from brand */}
      <Card withBorder padding="sm">
        <Text size="sm" fw={500} mb="xs" c="dimmed">
          From brand profile (edit in Settings tab)
        </Text>
        <Grid>
          <Grid.Col span={6}>
            <CopyableField label="Name" value={brand.name} copiedField={copiedField} onCopy={handleCopy}>
              <TextInput label="Name" value={brand.name} readOnly />
            </CopyableField>
          </Grid.Col>
          <Grid.Col span={6}>
            <CopyableField label="URL" value={brand.websiteUrl} copiedField={copiedField} onCopy={handleCopy}>
              <TextInput label="URL" value={brand.websiteUrl || ""} readOnly />
            </CopyableField>
          </Grid.Col>
          {brand.competitors?.length > 0 && (
            <Grid.Col span={12}>
              <CopyableField
                label="Competitors"
                value={brand.competitors.map((c) => c.name).join(", ")}
                copiedField={copiedField}
                onCopy={handleCopy}
              >
                <TextInput
                  label="Competitors / Alternatives"
                  value={brand.competitors.map((c) => c.name).join(", ")}
                  readOnly
                />
              </CopyableField>
            </Grid.Col>
          )}
        </Grid>
      </Card>

      <Divider label="Positioning for writing" labelPosition="left" />
      <Textarea
        label="Value propositions"
        description="What only you can say. Preferred over web sources in every article."
        placeholder="Lab-tested waterproofing, ultralight materials, lifetime warranty. Direct-to-consumer pricing without the markup."
        autosize
        minRows={3}
        maxRows={8}
        {...form.getInputProps("valuePropositions")}
      />

      {/* Editable submission content */}
      <Grid>
        <Grid.Col span={12}>
          <CopyableField label="Tagline" value={form.values.tagline} maxLength={60} copiedField={copiedField} onCopy={handleCopy}>
            <TextInput
              label="Tagline"
              placeholder="AI photo generator — headshots, portraits & more from selfies"
              {...form.getInputProps("tagline")}
            />
          </CopyableField>
        </Grid.Col>

        <Grid.Col span={12}>
          <CopyableField label="Short Description" value={form.values.shortDescription} maxLength={200} copiedField={copiedField} onCopy={handleCopy}>
            <Textarea
              label="Short Description"
              placeholder="Concise description for directories with character limits..."
              autosize
              minRows={2}
              maxRows={4}
              {...form.getInputProps("shortDescription")}
            />
          </CopyableField>
        </Grid.Col>

        <Grid.Col span={12}>
          <CopyableField label="Long Description" value={form.values.longDescription} maxLength={500} copiedField={copiedField} onCopy={handleCopy}>
            <Textarea
              label="Long Description"
              placeholder="Detailed description for directories that allow more text..."
              autosize
              minRows={3}
              maxRows={6}
              {...form.getInputProps("longDescription")}
            />
          </CopyableField>
        </Grid.Col>

        <Grid.Col span={6}>
          <CopyableField
            label="Pricing"
            value={
              form.values.pricingModel
                ? `${form.values.pricingModel}${form.values.price ? ` (${form.values.price})` : ""}`
                : ""
            }
            copiedField={copiedField}
            onCopy={handleCopy}
          >
            <Select
              label="Pricing Model"
              data={PRICING_MODELS}
              placeholder="Select pricing model"
              clearable
              {...form.getInputProps("pricingModel")}
            />
          </CopyableField>
        </Grid.Col>

        <Grid.Col span={6}>
          <CopyableField label="Price" value={form.values.price} copiedField={copiedField} onCopy={handleCopy}>
            <TextInput
              label="Price"
              placeholder="Starting $39.99/month"
              {...form.getInputProps("price")}
            />
          </CopyableField>
        </Grid.Col>

        <Grid.Col span={6}>
          <CopyableField label="Category" value={form.values.category} copiedField={copiedField} onCopy={handleCopy}>
            <TextInput
              label="Category"
              placeholder="AI Photography"
              {...form.getInputProps("category")}
            />
          </CopyableField>
        </Grid.Col>

        <Grid.Col span={12}>
          <CopyableField label="Tags" value={form.values.tags?.join(", ")} copiedField={copiedField} onCopy={handleCopy}>
            <TagsInput
              label="Tags / Keywords"
              placeholder="Add tags and press Enter"
              {...form.getInputProps("tags")}
            />
          </CopyableField>
        </Grid.Col>

        <Grid.Col span={12}>
          <CopyableField label="Maker's Comment" value={form.values.makersComment} maxLength={500} copiedField={copiedField} onCopy={handleCopy}>
            <Textarea
              label="Maker's Comment (for Product Hunt)"
              placeholder="Hey everyone! We built this because..."
              autosize
              minRows={3}
              maxRows={6}
              {...form.getInputProps("makersComment")}
            />
          </CopyableField>
        </Grid.Col>
      </Grid>

      <Divider label="Visual Style (AI Image Generation)" labelPosition="left" />

      <Card withBorder padding="sm">
        <Group justify="space-between" mb="md">
          <Text size="sm" c="dimmed">
            Configure a visual style to enable AI-generated images in blog content. Leave empty to generate text-only content.
          </Text>
          <Button
            variant="light"
            size="xs"
            leftSection={<IconSparkles size={14} />}
            onClick={handleGenerateVisualStyle}
            loading={isGeneratingVisualStyle}
            color="violet"
          >
            Suggest with AI
          </Button>
        </Group>
        <Grid>
          <Grid.Col span={6}>
            <TextInput
              label="Style"
              placeholder="minimalistic isometric, photorealistic, flat illustration..."
              {...form.getInputProps("visualStyle.style")}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              label="Color Palette"
              placeholder="two-tone blue and white, #1A73E8 and #FFFFFF..."
              {...form.getInputProps("visualStyle.colorPalette")}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              label="Mood"
              placeholder="professional and clean, playful and colorful..."
              {...form.getInputProps("visualStyle.mood")}
            />
          </Grid.Col>
          <Grid.Col span={6} />
          <Grid.Col span={12}>
            <Textarea
              label="Additional Instructions"
              placeholder="Never include people. Always use soft gradients..."
              autosize
              minRows={2}
              maxRows={4}
              {...form.getInputProps("visualStyle.additionalInstructions")}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <Textarea
              label="Default Image Prompt (JSON)"
              description="A JSON object merged into every image prompt. Per-image keys override these defaults."
              placeholder={'{ "colors": "#ccffaa and #00ff4a", "style": "minimalistic, isometric views" }'}
              autosize
              minRows={3}
              maxRows={8}
              styles={{ input: { fontFamily: "monospace", fontSize: 13 } }}
              error={
                form.values.visualStyle?.defaultImagePrompt &&
                (() => {
                  try { JSON.parse(form.values.visualStyle.defaultImagePrompt); return null; }
                  catch { return "Invalid JSON"; }
                })()
              }
              {...form.getInputProps("visualStyle.defaultImagePrompt")}
            />
          </Grid.Col>
        </Grid>
      </Card>

      {/* Content guidelines only feed the content pipeline, and the directory
          checklist belongs to the directories feature — both hidden for now. */}
      {isGlobalAdmin && (
        <>
          <Divider label="Content Guidelines" labelPosition="left" />

          <ContentGuidelinesEditor
            guidelines={form.values.contentGuidelines}
            onChange={(next) => form.setFieldValue("contentGuidelines", next)}
            defaultTranslationMode={form.values.defaultTranslationMode}
            onTranslationModeChange={(mode) => form.setFieldValue("defaultTranslationMode", mode)}
            brand={brand}
          />

          <Divider label="Directory Submissions" labelPosition="left" />

          <DirectoryChecklist
            directories={form.values.directories}
            onChange={handleDirectoriesChange}
          />
        </>
      )}
    </Stack>
  );
}
